import { Expense } from "@finway-group/shared/lib/models"
import { LINE_ITEM_QUANTITY_MAX_DECIMAL_PLACES } from "@finway-group/shared/lib/models/purchaseOrder/lineItem.validation"
import { isNumberWithinDecimalPlaces } from "@finway-group/shared/lib/utils/validators/input.validator"
import { Rule } from "antd/lib/form"
import { ExpenseFormContextInterface } from "~Components/modals/expenseCreateForm.context"
import { createLineItemRules } from "~Features/pages/purchaseOrders/components/LineItem.rules"
import i18n from "~Shared/locales/i18n"
import { isNotSet } from "~Shared/utils/helper.utils"
import { costCenterRules, getExpenseAccountRules, nonNegativeBasedOnStatusAndKind } from "./shared.rules"

export const validateSplitPercentage = (value: string) => {
    if (isNaN(value as any) || isNotSet(value)) {
        throw new Error(i18n.t("validation:number"))
    }
    const percentage = parseFloat(value)
    if (percentage >= 100) {
        throw new Error(i18n.t("validation:below.number", { max: 100 }))
    }
    if (percentage <= 0) {
        throw new Error(i18n.t("validation:greater.number", { min: 0 }))
    }
}

const validateSplitPercentagePromise = (value: string) => {
    try {
        validateSplitPercentage(value)
    } catch (err) {
        return Promise.reject(err.message)
    }
    return Promise.resolve()
}

interface SplitRulesProps {
    expense: Expense
    expenseForm: ExpenseFormContextInterface[0]["expenseForm"]
}

export const createSplitRules = ({ expense, expenseForm }: SplitRulesProps) => ({
    name: createLineItemRules().name as Array<Rule>,
    quantity: [
        {
            validator: (_rule, value: number) => {
                if (!isNotSet(value)) {
                    if (value <= 0) {
                        return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: 0 })))
                    }

                    if (!isNumberWithinDecimalPlaces(value, LINE_ITEM_QUANTITY_MAX_DECIMAL_PLACES)) {
                        return Promise.reject(new Error(i18n.t("validation:number_exceeding_decimal_places", { decimal_places: LINE_ITEM_QUANTITY_MAX_DECIMAL_PLACES })))
                    }

                    return Promise.resolve()
                }

                return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: 0 })))
            },
        },
    ] as Array<Rule>,
    percentage: [
        {
            validator: (_rule, value: string) => validateSplitPercentagePromise(value),
        },
    ] as Array<Rule>,
    unitPrice: [nonNegativeBasedOnStatusAndKind({ formInstance: expenseForm, isZeroAllowed: false })] as Array<Rule>,
    materialCode: createLineItemRules().materialCode as Array<Rule>,
    grossPrice: [
        {
            validator: (_rule, inputValue: string) => {
                const resolvedValue = inputValue ? inputValue.toString() : ""
                if (Number(resolvedValue) === 0) {
                    return Promise.reject(new Error(i18n.t("validation:not_zero")))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    netPrice: [nonNegativeBasedOnStatusAndKind({ formInstance: expenseForm, isZeroAllowed: false })] as Array<Rule>,
    taxPrice: [nonNegativeBasedOnStatusAndKind({ formInstance: expenseForm, isZeroAllowed: true })] as Array<Rule>,
    costCenter: costCenterRules() as Array<Rule>,
    expenseAccount: getExpenseAccountRules(expense) as Array<Rule>,
})
